// src/systems/bets.ts
//
// Phase 3 — pure bet resolution. Types-only deps.
// 0 always loses everything except a straight bet on 0 (which pays 35:1).
//
// Phase 13 (BETS-09): migrated to isLosingSlot + slot.kind === 'number' narrowing.
// Void slots lose ALL bets; number-zero loses all except straight-on-0.
// No hardcoded `n === 0` loss decisions remain — all delegated to isLosingSlot.

import type { BetType, BoardLayout, OutsideBetKind, Slot, SlotNumber } from '../state/types';
import { isLosingSlot } from './wheel';

const COLUMN_1: ReadonlySet<SlotNumber> = new Set([1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34]);
const COLUMN_2: ReadonlySet<SlotNumber> = new Set([2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35]);
const COLUMN_3: ReadonlySet<SlotNumber> = new Set([3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36]);

export interface BetResolution {
  hit: boolean;
  payout: number; // 0 on miss; on hit: 1 / 2 / 35 etc.
  /** Extra mult bonus from loop-back chains in dynamic dozen-sub resolution. */
  multBonus?: number;
}

export function basePayout(bet: BetType): number {
  switch (bet.kind) {
    case 'straight':
      return 35;
    case 'red':
    case 'black':
    case 'even':
    case 'odd':
    case 'low':
    case 'high':
      return 1;
    case 'dozen':
    case 'column':
      return 2;
    case 'split':
      return 17;
    case 'corner':
      return 8;
    case 'outside-sub':
      return bet.payout;
    case 'dozen-sub':
      // Dozen subcells pay 2:1 — same as the parent dozen bet.
      return 2;
  }
}

export function resolveBet(bet: BetType, slot: Slot, boardLayout?: BoardLayout): BetResolution {
  // Centralised void/zero loss decider per WHEEL-12 + BETS-09.
  // - Void slots lose ALL bets (no exceptions; void is a full loss).
  // - Number-zero loses non-straight bets (v1 invariant retained).
  if (isLosingSlot(slot, bet)) {
    return { hit: false, payout: 0 };
  }

  // After the guard above, the only path through is:
  //   slot.kind === 'number' AND (slot.value !== 0 OR bet.kind === 'straight')
  // TS narrowing: assert kind === 'number' to safely read value/color.
  if (slot.kind !== 'number') {
    // Defensive: should be unreachable if isLosingSlot returned false.
    return { hit: false, payout: 0 };
  }

  const n = slot.value;
  // Bleached (colorless) cell: the landed number counts as both red AND black.
  const isColorless =
    boardLayout != null &&
    boardLayout.cells.some((c) => c.bet.kind === 'straight' && c.bet.number === n && c.bleached);
  let hit = false;
  switch (bet.kind) {
    case 'straight':
      hit = bet.number === n;
      break;
    case 'red':
      hit = slot.color === 'red' || isColorless;
      break;
    case 'black':
      hit = slot.color === 'black' || isColorless;
      break;
    case 'even':
      hit = n !== 0 && n % 2 === 0;
      break;
    case 'odd':
      hit = n % 2 === 1;
      break;
    case 'low':
      hit = n >= 1 && n <= 18;
      break;
    case 'high':
      hit = n >= 19 && n <= 36;
      break;
    case 'dozen': {
      const w = bet.which;
      hit =
        (w === 1 && n >= 1 && n <= 12) ||
        (w === 2 && n >= 13 && n <= 24) ||
        (w === 3 && n >= 25 && n <= 36);
      break;
    }
    case 'column': {
      const w = bet.which;
      hit =
        (w === 1 && COLUMN_1.has(n)) ||
        (w === 2 && COLUMN_2.has(n)) ||
        (w === 3 && COLUMN_3.has(n));
      break;
    }
    case 'split':
      hit = bet.numbers.includes(n);
      break;
    case 'corner':
      hit = bet.numbers.includes(n);
      break;
    case 'outside-sub':
      // Resolve against the full parent category so each subcell behaves like the
      // original even-money bet. The `numbers` field identifies which half of the
      // parent this cell is (for positioning/chess-reach), but scoring uses parentKind
      // so placing on either half covers all numbers in that category.
      switch (bet.parentKind) {
        case 'red':
          hit = slot.color === 'red' || isColorless;
          break;
        case 'black':
          hit = slot.color === 'black' || isColorless;
          break;
        case 'even':
          hit = n !== 0 && n % 2 === 0;
          break;
        case 'odd':
          hit = n % 2 === 1;
          break;
        case 'low':
          hit = n >= 1 && n <= 18;
          break;
        case 'high':
          hit = n >= 19 && n <= 36;
          break;
      }
      break;
    case 'dozen-sub':
      // Dynamic resolution: look at what is currently in rows 1-3 of this
      // cell's column. Falls back to embedded numbers when boardLayout absent.
      if (boardLayout) {
        const dyn = resolveDozenSubDynamic(bet, slot, boardLayout, new Set());
        return { hit: dyn.hit, payout: dyn.hit ? basePayout(bet) : 0, multBonus: dyn.multBonus };
      }
      hit = bet.numbers.includes(n);
      break;
  }

  return { hit, payout: hit ? basePayout(bet) : 0 };
}

/**
 * Dynamic dozen-sub resolution. Instead of using the embedded `numbers` array,
 * look at what bets are currently in rows 1-3 of this cell's column and score
 * on those. Allows chains: a D2-sub cell swapped into D1's column means D1 also
 * covers D2's column content, etc. Loop detection via visitedCols (+1 mult each).
 */
function resolveDozenSubDynamic(
  bet: Extract<BetType, { kind: 'dozen-sub' }>,
  slot: Slot,
  boardLayout: BoardLayout,
  visitedCols: Set<number>,
): { hit: boolean; multBonus: number } {
  // Find this cell's current column (may differ from canonical if cell_swapper moved it)
  const cell = boardLayout.cells.find(
    (c) =>
      c.bet.kind === 'dozen-sub' &&
      (c.bet as typeof bet).parentWhich === bet.parentWhich &&
      (c.bet as typeof bet).subIdx === bet.subIdx,
  );
  if (!cell) return { hit: false, multBonus: 0 };

  const col = cell.col;
  if (visitedCols.has(col)) return { hit: false, multBonus: 1 }; // loop!
  visitedCols.add(col);

  let hit = false;
  let multBonus = 0;

  for (const row of [1, 2, 3] as const) {
    const neighbor = boardLayout.grid[row]?.[col];
    if (!neighbor) continue;
    const b = neighbor.bet;
    if (b.kind === 'straight') {
      if (slot.kind === 'number' && b.number === slot.value) hit = true;
    } else if (b.kind === 'dozen-sub') {
      const res = resolveDozenSubDynamic(b, slot, boardLayout, visitedCols);
      if (res.hit) hit = true;
      multBonus += res.multBonus;
    } else if (b.kind === 'outside-sub') {
      if (outsideSubHitsSlot(b.parentKind, slot)) hit = true;
    } else if (b.kind === 'column') {
      const cs = [null, COLUMN_1, COLUMN_2, COLUMN_3] as const;
      if (slot.kind === 'number' && cs[b.which]?.has(slot.value)) hit = true;
    }
  }

  return { hit, multBonus };
}

function outsideSubHitsSlot(kind: OutsideBetKind, slot: Slot): boolean {
  if (slot.kind !== 'number' || slot.value === 0) return false;
  const n = slot.value;
  switch (kind) {
    case 'red':
      return slot.color === 'red';
    case 'black':
      return slot.color === 'black';
    case 'even':
      return n % 2 === 0;
    case 'odd':
      return n % 2 === 1;
    case 'low':
      return n >= 1 && n <= 18;
    case 'high':
      return n >= 19 && n <= 36;
  }
}

// Helper for boss-modifier UI: which bet kinds are "even-money"?
// Phase 21 (BOARD-21): 'outside-sub' is also even-money — all sub-cells have payout 1:1.
export function isEvenMoneyBet(bet: BetType): boolean {
  return ['red', 'black', 'even', 'odd', 'low', 'high', 'outside-sub'].includes(bet.kind);
}

// Equality check for BetType — order-insensitive for split/corner numbers arrays.
// Exported so RoundScene.ts and tests can use the canonical implementation.
export function isSameBet(a: BetType, b: BetType): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'straight' && b.kind === 'straight') return a.number === b.number;
  if (a.kind === 'dozen' && b.kind === 'dozen') return a.which === b.which;
  if (a.kind === 'column' && b.kind === 'column') return a.which === b.which;
  if (a.kind === 'split' && b.kind === 'split') {
    const sa = [...a.numbers].sort((x, y) => x - y);
    const sb = [...b.numbers].sort((x, y) => x - y);
    return sa[0] === sb[0] && sa[1] === sb[1];
  }
  if (a.kind === 'corner' && b.kind === 'corner') {
    const sa = [...a.numbers].sort((x, y) => x - y);
    const sb = [...b.numbers].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
  }
  if (a.kind === 'outside-sub' && b.kind === 'outside-sub') {
    if (a.parentKind !== b.parentKind) return false;
    if (a.numbers.length !== b.numbers.length) return false;
    const sa = [...a.numbers].sort((x, y) => x - y);
    const sb = [...b.numbers].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
  }
  if (a.kind === 'dozen-sub' && b.kind === 'dozen-sub') {
    return a.parentWhich === b.parentWhich && a.subIdx === b.subIdx;
  }
  // even-money kinds with same kind are always the same bet
  return true;
}
